import React, { useState } from "react";
import { Box, Button, Link, Skeleton, Stack, Typography } from "@mui/material";
import { NewspaperOutlined, OpenInNew } from "@mui/icons-material";
import { useGetAllLocalNewsListQuery } from "../../../../Services/auto-complete.services";
import DashCard from "../../dashboard/DashCard";
import { DASH } from "../../dashboard/tokens";
import { timeAgo } from "../../../../Utils/timeAgo";

type Article = { title?: string; url?: string; publishedAt?: string; source?: string };

const SHOWN = 4;
const MAX = 10;

// Only ever link out to a real web page.
const safeUrl = (u?: string) => (u && /^https?:\/\//i.test(u) ? u : null);

/**
 * Local news for the home's city, in the dashboard's right column under the
 * map (moved out of Property Details in October 2026 so it's on every tab).
 * Google News RSS via /api/property/news/:city,state — public, like before.
 */
const LocalNews: React.FC<{ city?: string; state?: string }> = ({ city, state }) => {
  const place = [city, state].filter(Boolean).join(", ");
  const { data, isLoading, isError, refetch } = useGetAllLocalNewsListQuery(`${city},${state}`, {
    skip: !city || !state,
  });
  const [expanded, setExpanded] = useState(false);

  if (!city || !state) return null;

  const articles: Article[] = (data?.data?.result || []).filter((a: Article) => a?.title).slice(0, MAX);
  const shown = expanded ? articles : articles.slice(0, SHOWN);

  return (
    <DashCard
      title="Local news"
      subtitle={place}
      icon={<NewspaperOutlined fontSize="small" />}
      testId="local-news"
    >
      {isLoading ? (
        <Stack spacing={3}>
          {[0, 1, 2].map((i) => (
            <Box key={i}>
              <Skeleton variant="text" width="95%" />
              <Skeleton variant="text" width="70%" />
              <Skeleton variant="text" width="35%" height={14} />
            </Box>
          ))}
        </Stack>
      ) : isError ? (
        <Stack spacing={1} alignItems="flex-start">
          <Typography variant="body2" sx={{ color: DASH.muted }}>
            Couldn't load local news right now.
          </Typography>
          <Button size="small" onClick={() => refetch()} sx={{ color: DASH.teal, fontWeight: 700, px: 0 }}>
            Try again
          </Button>
        </Stack>
      ) : articles.length === 0 ? (
        <Typography variant="body2" sx={{ color: DASH.muted }} data-testid="local-news-empty">
          No recent local news for {place}.
        </Typography>
      ) : (
        <>
          <Stack component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {shown.map((a, i) => {
              const href = safeUrl(a.url);
              const meta = [a.source, a.publishedAt ? timeAgo(a.publishedAt) : null].filter(Boolean).join(" · ");
              return (
                <Box
                  component="li"
                  key={a.url || i}
                  sx={{ py: 2.5, borderTop: i === 0 ? "none" : `1px solid ${DASH.border}` }}
                  data-testid="local-news-item"
                >
                  <Link
                    href={href || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    sx={{
                      color: "#1d2b2b",
                      display: "block",
                      "&:hover .news-title": { color: DASH.teal, textDecoration: "underline" },
                    }}
                  >
                    <Typography
                      className="news-title"
                      sx={{
                        fontWeight: 700,
                        fontSize: 14,
                        lineHeight: 1.4,
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {a.title}
                    </Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75 }}>
                      <Typography sx={{ fontSize: 12, color: DASH.muted }} noWrap>
                        {meta}
                      </Typography>
                      {href && <OpenInNew sx={{ fontSize: 12, color: DASH.muted }} aria-label="Opens in a new tab" />}
                    </Stack>
                  </Link>
                </Box>
              );
            })}
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
            {articles.length > SHOWN ? (
              <Button
                size="small"
                onClick={() => setExpanded((e) => !e)}
                sx={{ color: DASH.teal, fontWeight: 700, px: 0 }}
                data-testid="local-news-toggle"
              >
                {expanded ? "Show less" : `Show ${articles.length - SHOWN} more`}
              </Button>
            ) : (
              <span />
            )}
            <Typography sx={{ fontSize: 11, color: DASH.muted }}>via Google News</Typography>
          </Stack>
        </>
      )}
    </DashCard>
  );
};

export default LocalNews;
